import { slideMasters } from '@features/executive-report/utils/slide-masters';
import { ShapeProps, Slide, TextProps } from '@features/powerpoint';
import { cardBackground } from './components/card-background';
import { formatters } from '@utils/formattersV2';
import { themeV2 as theme } from '@features/executive-report/domain/theme-v2';

const SECTION_CARD_Y = 2.85;
const SECTION_CARD_X = 4.09;
const SECTION_CARD_X_OFFSET = 2.22;
const SECTION_CARD_TITLE_Y = 3.11;
const SECTION_CARD_W = 2.1;
const SECTION_LINE_Y = 3.65;
const SECTION_INNER_SHAPE_Y = 3.86;
const DIMENSION_COUNT_Y = 4.03;
const DIMENSION_NAME_Y = 4.68;
const METRIC_Y_OFFSET = 0.64;

const sectionCard = (
  position: Pick<ShapeProps['options'], 'x' | 'y'>
): ShapeProps =>
  cardBackground({
    ...position,
    h: 2.5,
    w: SECTION_CARD_W,
  });

const sectionLine = (
  position: Pick<ShapeProps['options'], 'x' | 'y'>
): ShapeProps => ({
  shapeName: 'line',
  options: {
    line: { color: theme.colors.metricBlue, width: 0.78 },
    h: 0,
    w: 1.77,
    ...position,
  },
});

const sectionInnerShape = (
  position: Pick<ShapeProps['options'], 'x' | 'y'>
): ShapeProps => ({
  shapeName: 'roundRect',
  options: {
    ...position,
    h: 1.29,
    w: 1.77,
    fill: {
      type: 'solid',
      color: theme.colors.inmarketGreen,
      transparency: 87,
    },
    rectRadius: 0.05,
  },
});

const buildNameValuePairs = (
  props: SlideProps
): { name: string; value: TextProps['text'] }[] => [
  {
    name: 'Ad Spend',
    value: formatters.currency(props.adSpend, { full: true, decimals: 0 }),
  },
  {
    name: 'Flight Dates',
    value: [
      {
        text: props.flightDates[0],
      },
      {
        text: ' » ',
        options: {
          color: theme.colors.inmarketGreen,
        },
      },
      {
        text: props.flightDates[1],
      },
    ],
  },
  {
    name: 'Measurement Period',
    value: [
      {
        text: props.measurementPeriod[0],
      },
      {
        text: ' » ',
        options: {
          color: theme.colors.inmarketGreen,
        },
      },
      {
        text: props.measurementPeriod[1],
      },
    ],
  },
  {
    name: 'Unique Users Reached',
    value: formatters.number(props.uniqueUsers, { full: true }),
  },
  {
    name: 'Impressions',
    value: formatters.number(props.impressions, { full: true }),
  },
];

type SlideProps = {
  navbarText: TextProps;
  adSpend: number;
  flightDates: [string, string];
  measurementPeriod: [string, string];
  uniqueUsers: number;
  impressions: number;
  sections: {
    name: string;
    count: number;
    dimensionName: string;
  }[];
};

export const salesCampaignOverviewSlide = (props: SlideProps): Slide => {
  return {
    slideMaster: slideMasters.light,
    color: theme.colors.metricBlue,
    texts: [
      props.navbarText,
      {
        text: 'Campaign Overview',
        options: {
          fontSize: 36,
          bold: true,
          h: 0.75,
          w: 12.67,
          x: 0.33,
          y: 0.59,
        },
      },
      ...buildNameValuePairs(props).flatMap(
        ({ name, value }, index): TextProps[] => {
          return [
            {
              text: name.toLocaleUpperCase(),
              options: {
                fontSize: 9,
                h: 0.25,
                w: 3.12,
                x: 0.71,
                y: 2.48 + index * METRIC_Y_OFFSET,
              },
            },
            {
              text: value,
              options: {
                fontSize: 16,
                bold: true,
                h: 0.5,
                w: 3.12,
                x: 0.71,
                y: 2.66 + index * METRIC_Y_OFFSET,
              },
            },
          ];
        }
      ),
      ...props.sections.flatMap(
        ({ name, count, dimensionName }, index): TextProps[] => {
          return [
            {
              text: [
                {
                  text: 'The ',
                },
                {
                  text: name.toLocaleUpperCase(),
                  options: {
                    color: theme.colors.inmarketGreen,
                    bold: true,
                  },
                },
              ],
              options: {
                fontSize: 21.7,
                align: 'center',
                h: 0.41,
                w: SECTION_CARD_W,
                x: SECTION_CARD_X + index * SECTION_CARD_X_OFFSET,
                y: SECTION_CARD_TITLE_Y,
              },
            },
            {
              text: formatters.number(count, { full: true }),
              options: {
                fontSize: 31,
                align: 'center',
                h: 0.6,
                w: SECTION_CARD_W,
                x: SECTION_CARD_X + index * SECTION_CARD_X_OFFSET,
                y: DIMENSION_COUNT_Y,
              },
            },
            {
              text: dimensionName.toLocaleUpperCase(),
              options: {
                fontSize: 13.9,
                bold: true,
                align: 'center',
                h: 0.29,
                w: SECTION_CARD_W,
                x: SECTION_CARD_X + index * SECTION_CARD_X_OFFSET,
                y: DIMENSION_NAME_Y,
              },
            },
          ];
        }
      ),
    ],
    shapes: [
      {
        shapeName: 'line',
        options: {
          line: { color: theme.colors.inmarketGreen, width: 1.5 },
          h: 3.23,
          w: 0,
          x: 0.45,
          y: 2.48,
        },
      },
      ...props.sections.flatMap((_, index): ShapeProps[] => {
        return [
          sectionCard({
            x: SECTION_CARD_X + index * SECTION_CARD_X_OFFSET,
            y: SECTION_CARD_Y,
          }),
          sectionLine({
            x: 4.28 + index * SECTION_CARD_X_OFFSET,
            y: SECTION_LINE_Y,
          }),
          sectionInnerShape({
            x: 4.25 + index * SECTION_CARD_X_OFFSET,
            y: SECTION_INNER_SHAPE_Y,
          }),
        ];
      }),
    ],
  };
};
